/**
 * The browser tick store — ROADMAP 3.13, ARCHITECTURE.md §4.7.
 *
 * Mirror typed arrays, outside React. The §3.2 stack table calls this
 * "non-negotiable" and the reason is arithmetic: 460 chain cells reconciling
 * at 10 Hz is 4,600 cell-renders a second and it will jank. Zustand holds UI
 * state — which chain, which account, which filter — and never prices.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * HOW A TICK REACHES ONE <td> AND NOTHING ELSE
 *
 *   frame arrives -> written into typed arrays -> that slot's listeners fire
 *
 * Each cell subscribes to its own slot through `useSyncExternalStore`. A tick
 * on NIFTY 25000 CE notifies the listeners for that slot and no others, so
 * React re-renders one cell. There is no context, no reducer and no store
 * object that changes identity — all three would re-render the subtree.
 *
 * `useSyncExternalStore` specifically, not `useEffect` + `useState`: it is the
 * API that exists for exactly this, and it is tear-free. With `useState` a
 * concurrent render can read slot 12 before an update and slot 13 after it,
 * and a chain showing two different instants is a chain showing a spread that
 * never existed.
 *
 * WHY A VERSION NUMBER PER SLOT
 *
 * `getSnapshot` must return a value that is `Object.is`-stable between real
 * changes, or React loops. Returning the price itself almost works and breaks
 * on a tick that does not change the price — common on an illiquid strike,
 * where OI moves and the price does not. A monotonic counter per slot is
 * stable, cheap, and changes exactly when something did.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import {
  FRAME_VERSION,
  TICK_FIELD_COUNT,
  TICK_FIELD_INDEX,
  isFrameError,
  readFrame,
  stalenessOf,
  type Staleness,
  type TickField,
} from '@apna/shared'

export interface TickSnapshot {
  token: number
  ltp: number
  bid: number
  ask: number
  open: number
  high: number
  low: number
  close: number
  vwap: number
  oi: number
  exchTs: number
  recvTs: number
  /** Changes on every applied frame for this slot. The subscription key. */
  version: number
}

/**
 * Where a slot's current value came from.
 *
 * §4.8a: a chain switch paints a REST snapshot immediately and upgrades to
 * live when the first frame arrives. A cell has to be able to say which it is
 * holding — a snapshot is a real price and a true statement, but it is not a
 * LIVE one, and rendering the two identically is the kind of quiet dishonesty
 * this product keeps refusing.
 */
export const TICK_SOURCE = {
  NONE: 0,
  /** A REST quote fetched during market hours, awaiting its first live tick. */
  SNAPSHOT: 1,
  /** Streaming. */
  LIVE: 2,
  /**
   * The last traded price of a session that has ENDED.
   *
   * Distinct from SNAPSHOT because they mean different things to a reader: a
   * snapshot is a moment behind and about to be replaced, a close is final
   * until the market reopens. Rendering them the same way would tell someone
   * at 20:00 that a price is merely lagging.
   */
  CLOSE: 3,
  /** Streaming from the admin-controlled synthetic market session. */
  SIMULATION: 4,
} as const
export type TickSource = (typeof TICK_SOURCE)[keyof typeof TICK_SOURCE]

/** Staleness bands as small integers, so a change is an integer comparison. */
const BAND = { NONE: 0, FRESH: 1, LAGGING: 2, STALE: 3, DEAD: 4 } as const

const bandOf = (s: Staleness): number =>
  s === 'FRESH' ? BAND.FRESH : s === 'LAGGING' ? BAND.LAGGING : s === 'STALE' ? BAND.STALE : BAND.DEAD

export interface TickStoreStats {
  capacity: number
  tracked: number
  framesApplied: number
  recordsApplied: number
  unknownTokens: number
  rejectedFrames: number
  lastFrameAt: number
  /** Set when the engine sends a frame version this build cannot read. */
  versionMismatch: boolean
  snapshotsApplied: number
  /** Cells that changed staleness band without ticking. */
  stalenessChanges: number
}

type Listener = () => void

export class BrowserTickStore {
  readonly capacity: number

  private readonly tokens: Int32Array
  private readonly values: Float64Array
  private readonly version: Uint32Array
  /**
   * The staleness band each slot was last seen at.
   *
   * Lets the staleness watch notify ONLY the slots that crossed a boundary,
   * rather than waking every cell on a timer.
   */
  private readonly band: Uint8Array
  /** Where each slot's value came from. See `TICK_SOURCE`. */
  private readonly source: Uint8Array
  private readonly slotOf = new Map<number, number>()
  private readonly listeners = new Map<number, Set<Listener>>()
  /** Fires on any change. For headers and counters, not for cells. */
  private readonly globalListeners = new Set<Listener>()

  private nextSlot = 0
  private streamingSource: TickSource = TICK_SOURCE.LIVE
  private epoch = 0
  private s = {
    framesApplied: 0,
    recordsApplied: 0,
    unknownTokens: 0,
    rejectedFrames: 0,
    lastFrameAt: 0,
    versionMismatch: false,
    snapshotsApplied: 0,
    stalenessChanges: 0,
  }

  constructor(capacity = 1_024) {
    this.capacity = capacity
    this.tokens = new Int32Array(capacity)
    this.values = new Float64Array(capacity * TICK_FIELD_COUNT)
    this.version = new Uint32Array(capacity)
    this.band = new Uint8Array(capacity)
    this.source = new Uint8Array(capacity)
  }

  /**
   * Reserve a slot for a token.
   *
   * Called when a cell mounts, so the slot exists before the first frame does
   * and `getSnapshot` has something stable to return. Returns -1 when full;
   * the caller renders the cell as having no data rather than throwing.
   */
  track(token: number): number {
    const existing = this.slotOf.get(token)
    if (existing !== undefined) return existing
    if (this.nextSlot >= this.capacity) return -1

    const slot = this.nextSlot
    this.nextSlot += 1
    this.tokens[slot] = token
    this.band[slot] = BAND.NONE
    this.source[slot] = TICK_SOURCE.NONE
    this.slotOf.set(token, slot)
    return slot
  }

  slotFor(token: number): number {
    return this.slotOf.get(token) ?? -1
  }

  /**
   * Apply one binary frame.
   *
   * Nothing is allocated per record: the frame reader hands over primitives
   * and they go straight into the arrays.
   */
  apply(data: ArrayBuffer | ArrayBufferView): void {
    const view = data instanceof ArrayBuffer ? new Uint8Array(data) : data
    const touched: number[] = []

    const header = readFrame(view, (token, field) => {
      const slot = this.slotOf.get(token)
      if (slot === undefined) {
        // A token nothing on screen asked for. Counted, not an error: the
        // gateway filters per connection, so this means a cell unmounted
        // between the subscribe and the frame.
        this.s.unknownTokens += 1
        return
      }
      const base = slot * TICK_FIELD_COUNT
      for (let f = 0; f < TICK_FIELD_COUNT; f += 1) {
        const value = field(f)
        // A partial/empty quote must never erase a known traded price.
        if (f === TICK_FIELD_INDEX.ltp && !(value > 0)) continue
        this.values[base + f] = value
      }
      this.version[slot] = (this.version[slot]! + 1) >>> 0
      // A live frame upgrades a snapshot, and never the other way round.
      this.source[slot] = this.streamingSource
      this.band[slot] = bandOf(stalenessOf(this.values[base + TICK_FIELD_INDEX.exchTs]!, Date.now()))
      touched.push(slot)
      this.s.recordsApplied += 1
    })

    if (isFrameError(header)) {
      this.s.rejectedFrames += 1
      if (header === 'VERSION_MISMATCH') {
        // The engine was deployed and speaks a newer frame. Going quiet and
        // saying so beats decoding another build's bytes into prices.
        this.s.versionMismatch = true
      }
      this.notifyGlobal()
      return
    }

    this.s.framesApplied += 1
    this.s.lastFrameAt = Date.now()

    // Notified AFTER every write in the frame has landed, so a cell cannot
    // read a frame that is half applied.
    for (const slot of touched) this.notifySlot(slot)
    this.notifyGlobal()
  }

  /** Subscribe to one slot. The return value unsubscribes. */
  subscribeSlot(slot: number, fn: Listener): () => void {
    let set = this.listeners.get(slot)
    if (!set) {
      set = new Set()
      this.listeners.set(slot, set)
    }
    set.add(fn)
    return () => {
      set!.delete(fn)
      if (set!.size === 0) this.listeners.delete(slot)
    }
  }

  subscribeAll(fn: Listener): () => void {
    this.globalListeners.add(fn)
    return () => this.globalListeners.delete(fn)
  }

  private notifyGlobal(): void {
    for (const fn of [...this.globalListeners]) fn()
  }

  private notifySlot(slot: number): void {
    // React can unsubscribe/re-subscribe while a notification is delivered.
    // Iterating the mutable Set can revisit the new listener indefinitely.
    for (const fn of [...(this.listeners.get(slot) ?? [])]) fn()
  }

  /**
   * Paint a REST snapshot (§4.8a).
   *
   * Never overwrites a LIVE value: a snapshot is fetched in parallel with the
   * subscribe, and the two race. A snapshot landing after the first live tick
   * would move the price BACKWARDS by however long the HTTP call took, which
   * on a fast-moving strike is a visibly wrong number.
   */
  applySnapshot(
    token: number,
    fields: Partial<Record<TickField, number>>,
    at: number,
    source: TickSource = TICK_SOURCE.SNAPSHOT,
  ): boolean {
    const slot = this.track(token)
    if (slot < 0) return false
    if (this.source[slot] === TICK_SOURCE.LIVE || this.source[slot] === TICK_SOURCE.SIMULATION) return false

    /*
     * A ZERO NEVER OVERWRITES A PRICE WE ALREADY HAVE.
     *
     * [B12]'s rule, which the tick decoder already follows for partial
     * frames, applied to snapshots. It matters because this gateway answers
     * `/instruments/quotes` OUTSIDE MARKET HOURS with a correctly-shaped,
     * entirely zero payload — measured 2026-10-07 17:22 IST, where NIFTY 50,
     * NIFTY BANK and FIN SERVICE all came back
     * `ltp=0 open=0 high=0 low=0 close=0 exchTs=0`.
     *
     * Without this, one such reply wipes a real close off the screen and the
     * cell goes from showing a price to showing a dash. Having no price is a
     * dash; being TOLD nothing is not the same as being told zero.
     *
     * Zero is skipped outright rather than compared, because no field here
     * carries information as a zero: an option genuinely worth five paise
     * reports 0.05, and a contract with no open interest is indistinguishable
     * from one the gateway declined to tell us about. The slot is already
     * zero-initialised, so skipping leaves exactly the right value.
     */
    const base = slot * TICK_FIELD_COUNT
    let changed = 0
    for (const [name, value] of Object.entries(fields)) {
      if (typeof value !== 'number' || !Number.isFinite(value)) continue
      const index = base + TICK_FIELD_INDEX[name as TickField]
      // A zero never replaces something. A zero over a zero is not a change.
      if (value === 0) continue
      if (this.values[index] === value) continue
      this.values[index] = value
      changed += 1
    }

    /*
     * A snapshot that said nothing is not a snapshot.
     *
     * Returning early leaves the slot's source, version and listeners
     * untouched: the cell keeps whatever it had, nothing re-renders, and the
     * slot does not start claiming to hold a REST price it never received.
     * The token is still tracked above, so a later real price lands in it.
     */
    if (changed === 0) return false
    if (!(this.values[base + TICK_FIELD_INDEX.exchTs]! > 0)) {
      this.values[base + TICK_FIELD_INDEX.exchTs] = at
    }
    this.values[base + TICK_FIELD_INDEX.recvTs] = at

    this.source[slot] = source
    this.version[slot] = (this.version[slot]! + 1) >>> 0
    this.band[slot] = bandOf(stalenessOf(this.values[base + TICK_FIELD_INDEX.exchTs]!, at))
    this.s.snapshotsApplied += 1

    this.notifySlot(slot)
    this.notifyGlobal()
    return true
  }

  sourceOf(slot: number): TickSource {
    return slot < 0 ? TICK_SOURCE.NONE : (this.source[slot] as TickSource)
  }

  setStreamingSource(source: TickSource): void {
    this.streamingSource = source
  }

  /** Drop prices from the previous market context while retaining reservations. */
  reset(): void {
    this.epoch += 1
    this.values.fill(0)
    for (const slot of this.slotOf.values()) {
      this.source[slot] = TICK_SOURCE.NONE
      this.band[slot] = BAND.NONE
      this.version[slot] = (this.version[slot]! + 1) >>> 0
      this.notifySlot(slot)
    }
    this.s.lastFrameAt = 0
    this.notifyGlobal()
  }

  marketEpoch(): number { return this.epoch }

  /**
   * Re-evaluate staleness and notify ONLY the cells that crossed a band.
   *
   * ROADMAP 3.15, and the bug it fixes: a cell re-renders when its instrument
   * TICKS, so an instrument that goes SILENT keeps rendering its last price
   * with fresh styling forever. That is precisely the case per-cell staleness
   * exists to catch — a broker-side eviction freezes one instrument while the
   * chain around it updates perfectly ([B3]) — and without this the defence
   * does not work at all.
   *
   * Notifying only on a band CHANGE is what keeps it cheap: a chain of 460
   * live cells crosses no boundaries, so a sweep costs 460 integer
   * comparisons and zero renders. Waking every cell on a timer would undo
   * 3.14's whole result.
   */
  sweepStaleness(now = Date.now()): number {
    let changed = 0
    for (const slot of this.slotOf.values()) {
      const exchTs = this.values[slot * TICK_FIELD_COUNT + TICK_FIELD_INDEX.exchTs]!
      const next = exchTs > 0 ? bandOf(stalenessOf(exchTs, now)) : BAND.DEAD
      if (next === this.band[slot]) continue
      this.band[slot] = next
      // The version moves so `useSyncExternalStore` sees a new snapshot. The
      // VALUE has not changed — only how old it is — and that is a real
      // change for anything rendering freshness.
      this.version[slot] = (this.version[slot]! + 1) >>> 0
      changed += 1
      this.notifySlot(slot)
    }
    if (changed > 0) {
      this.s.stalenessChanges += changed
      this.notifyGlobal()
    }
    return changed
  }

  /** The subscription key for a slot. Stable until something changes. */
  versionOf(slot: number): number {
    return slot < 0 ? 0 : this.version[slot]!
  }

  field(slot: number, name: TickField): number {
    if (slot < 0) return 0
    return this.values[slot * TICK_FIELD_COUNT + TICK_FIELD_INDEX[name]]!
  }

  /**
   * A whole row.
   *
   * Allocates, so it is NOT what a hot cell should call — a cell reads the one
   * field it renders. This is for a detail panel or a test.
   */
  snapshot(token: number): TickSnapshot | null {
    const slot = this.slotOf.get(token)
    if (slot === undefined) return null
    const base = slot * TICK_FIELD_COUNT
    const v = this.values
    return {
      token,
      ltp: v[base + TICK_FIELD_INDEX.ltp]!,
      bid: v[base + TICK_FIELD_INDEX.bid]!,
      ask: v[base + TICK_FIELD_INDEX.ask]!,
      open: v[base + TICK_FIELD_INDEX.open]!,
      high: v[base + TICK_FIELD_INDEX.high]!,
      low: v[base + TICK_FIELD_INDEX.low]!,
      close: v[base + TICK_FIELD_INDEX.close]!,
      vwap: v[base + TICK_FIELD_INDEX.vwap]!,
      oi: v[base + TICK_FIELD_INDEX.oi]!,
      exchTs: v[base + TICK_FIELD_INDEX.exchTs]!,
      recvTs: v[base + TICK_FIELD_INDEX.recvTs]!,
      version: this.version[slot]!,
    }
  }

  stalenessOfSlot(slot: number, now = Date.now()): Staleness {
    if (slot < 0) return 'DEAD'
    return stalenessOf(this.field(slot, 'exchTs'), now)
  }

  stats(): TickStoreStats {
    return { capacity: this.capacity, tracked: this.slotOf.size, ...this.s }
  }

  /** The frame version this build can read. For the diagnostics panel. */
  readonly frameVersion = FRAME_VERSION
}

/**
 * The one store for the tab.
 *
 * A module singleton rather than a React context: a context value that changes
 * re-renders its whole subtree, which is the exact thing this file exists to
 * avoid. Nothing about the store's identity ever changes, so there is nothing
 * for React to react to.
 */
export const tickStore = new BrowserTickStore(8_192)
